/* eslint-disable react-refresh/only-export-components -- 帳票の描画コンポーネントは openShiftPrintWindow から内部利用するためのもの */
import { renderToStaticMarkup } from 'react-dom/server'
import type { ScheduledTask_Output, ProductionSequenceItem, StaffMember } from '@/api'
import type { WeekOption, ShiftAssignments, ShiftType } from '../hooks/useDetailedPlanFlow'

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']
const SHIFTS: ShiftType[] = ['day', 'evening', 'night']

// スタッフ名ではなく枠を表す名前。休み欄の対象外にする。
const PLACEHOLDER_STAFF_NAMES = ['ストッカー', 'オペレーター']

// 帳票（印刷）用の成形機の並び順。1号機=J850AD-1, 2号機=J850AD-2, ...
const PRINT_MACHINE_ORDER = ['J850AD-1', 'J850AD-2', 'J650AD-2', 'J650AD-1', 'J550AD', 'J450AD', 'αS300', '100EVA']

// 段替え順の丸数字（①②③...）。20を超える場合は (n) 形式にフォールバック。
const circledNumber = (n: number): string => (n >= 1 && n <= 20 ? String.fromCharCode(0x2460 + n - 1) : `(${n})`)

// 生産順が未算出（古いレスポンス）の場合に、タスクからSKU単位の行を組み立てるフォールバック。
// 前日からの引き継ぎが分からないため段替え番号は付けない。
const fallbackItems = (tasks: ScheduledTask_Output[]): ProductionSequenceItem[] => {
  const items: ProductionSequenceItem[] = []
  for (const t of tasks) {
    const found = items.find((i) => i.sku_id === t.sku_id)
    if (found) {
      found.quantity += t.quantity
      found.quantity_total += t.quantity_total
      found.duration_sec += t.duration_sec
      found.task_ids = [...(found.task_ids ?? []), t.task_id]
      continue
    }
    items.push({
      sku_id: t.sku_id,
      order_no: items.length + 1,
      quantity: t.quantity,
      quantity_total: t.quantity_total,
      duration_sec: t.duration_sec,
      task_ids: [t.task_id],
      mold_change_no: null,
    })
  }
  return items
}

// A4横・余白6mmの印刷可能領域（96dpi換算）。1ページに確実に収めるため少し小さめに取る。
const CONTENT_WIDTH = 1070
const PAGE_HEIGHT = 740

export interface PrintViewProps {
  week: WeekOption
  machineIds: string[]
  tasksByDateMachine: Record<string, Record<string, ScheduledTask_Output[]>>
  // 日付 → 成形機 → 生産順（段替え番号付き）。サーバ算出。
  sequencesByDateMachine: Record<string, Record<string, ProductionSequenceItem[]>>
  assignments: ShiftAssignments
  staffList: StaffMember[]
  note?: string
}

const PrintView: React.FC<PrintViewProps> = ({
  week,
  machineIds,
  tasksByDateMachine,
  sequencesByDateMachine,
  assignments,
  staffList,
  note,
}) => {
  const dates = [...week.dates].sort()

  // 印刷対象の成形機：規定順 + データにしか無い成形機を末尾に追加
  const machines = [...PRINT_MACHINE_ORDER]
  for (const mid of machineIds) {
    if (!machines.includes(mid)) machines.push(mid)
  }
  for (const date of dates) {
    for (const mid of Object.keys(tasksByDateMachine[date] ?? {})) {
      if (!machines.includes(mid)) machines.push(mid)
    }
  }

  // 実在のスタッフではない枠（プレースホルダー）は休み欄に出さない
  const allStaffNames = [...new Set(staffList.map((s) => s.name))].filter(
    (name) => !PLACEHOLDER_STAFF_NAMES.includes(name),
  )

  // その成形機・その日に割り当てられた検査員（全シフトを結合、重複排除）
  const inspectorsFor = (date: string, machineId: string): string[] => {
    const names: string[] = []
    for (const shift of SHIFTS) {
      for (const n of assignments[date]?.[shift]?.[machineId] ?? []) {
        if (n && !names.includes(n)) names.push(n)
      }
    }
    return names
  }

  // その日どこかのシフト・成形機に割り当てられているスタッフ名の集合
  const assignedOn = (date: string): Set<string> => {
    const set = new Set<string>()
    const dayData = assignments[date]
    if (dayData) {
      for (const shift of SHIFTS) {
        for (const slot of Object.values(dayData[shift] ?? {})) {
          for (const n of slot) if (n) set.add(n)
        }
      }
    }
    return set
  }

  const isClosed = (date: string): boolean => assignedOn(date).size === 0
  const isWeekday = (date: string): boolean => {
    const day = new Date(date).getDay()
    return day >= 1 && day <= 5
  }
  // 休業日（誰も割り当てられていない日）は列ごと描画しない。ただし平日（月〜金）は休業でも表示する。
  const openDates = dates.filter((date) => !isClosed(date) || isWeekday(date))

  const bodyRowCount = machines.length + 1 // 成形機行 + 休みスタッフ行

  return (
    <>
      <h1>生産シフト帳票{'　'}{week.label}</h1>
      <p className="sub">
        ※ SKUは生産順に記載（数量は不良込みの成形総数）。丸数字（①②③…）は段替えの発生箇所とその順番
      </p>
      <table>
        <thead>
          <tr>
            <th className="mach">成形機</th>
            {openDates.map((date) => {
              const d = new Date(date)
              return (
                <th key={date} className="day-col">
                  {date.slice(5).replace(/-/g, '/')}（{WEEKDAY_LABELS[d.getDay()]}）
                </th>
              )
            })}
            <th className="tokki">特記</th>
          </tr>
        </thead>
        <tbody>
          {machines.map((mid, mi) => (
            <tr key={mid}>
              <th className="mach">
                <span className="gono">{mi + 1}号機</span>
                <br />
                <span className="mname">{mid}</span>
              </th>
              {openDates.map((date) => {
                const tasks = tasksByDateMachine[date]?.[mid] ?? []
                const items = sequencesByDateMachine[date]?.[mid] ?? fallbackItems(tasks)
                const inspectors = inspectorsFor(date, mid)
                return (
                  <td key={date}>
                    {items.map((item) => (
                      <div key={`${item.sku_id}-${item.order_no}`} className="task">
                        <span className="sku">{item.sku_id}</span>
                        {item.mold_change_no != null && (
                          <span className="molno">{circledNumber(item.mold_change_no)}</span>
                        )}
                        <span className="qty">{item.quantity_total.toLocaleString()}</span>
                      </div>
                    ))}
                    {inspectors.length > 0 && <div className="insp">検：{inspectors.join('、')}</div>}
                  </td>
                )
              })}
              {mi === 0 && (
                <td className="tokki" rowSpan={bodyRowCount}>
                  {note}
                </td>
              )}
            </tr>
          ))}
          <tr className="offrow">
            <th className="mach">休み</th>
            {openDates.map((date) => {
              const assigned = assignedOn(date)
              // 休業日（平日の休業）は名前を列挙せず「休業」と表示
              if (assigned.size === 0) {
                return (
                  <td key={date} className="day-off closed">
                    休業
                  </td>
                )
              }
              const off = allStaffNames.filter((n) => !assigned.has(n))
              return (
                <td key={date} className="day-off">
                  {off.length > 0 ? off.join('、') : '—'}
                </td>
              )
            })}
          </tr>
        </tbody>
      </table>
    </>
  )
}

const PRINT_STYLES = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Meiryo', 'Yu Gothic', 'MS PGothic', sans-serif; color: #111; }
  @page { size: A4 landscape; margin: 6mm; }
  #page { position: relative; width: ${CONTENT_WIDTH}px; height: ${PAGE_HEIGHT}px; overflow: hidden; margin: 0 auto; }
  #content { position: absolute; top: 0; left: 0; width: ${CONTENT_WIDTH}px; transform-origin: top left; }
  h1 { font-size: 15px; margin: 0 0 6px; }
  .sub { font-size: 11px; color: #444; margin: 0 0 8px; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { border: 1px solid #333; padding: 3px 4px; vertical-align: top; font-size: 10px; }
  thead th { background: #eee; text-align: center; font-size: 11px; }
  .mach { width: 70px; font-weight: bold; text-align: center; background: #f6f6f6; }
  .gono { font-size: 11px; }
  .mname { font-size: 10px; color: #333; font-weight: normal; }
  .tokki { width: 88px; background: #fff; white-space: pre-wrap; word-break: break-word; }
  .task { display: flex; align-items: baseline; gap: 4px; line-height: 1.35; }
  .molno { font-weight: bold; flex-shrink: 0; }
  .sku { font-weight: bold; }
  .qty { margin-left: auto; font-variant-numeric: tabular-nums; }
  .insp { margin-top: 2px; color: #0055aa; font-size: 9px; }
  .offrow th { background: #eef1fa; }
  .day-off { font-size: 10px; color: #333; }
  .day-off.closed { text-align: center; font-weight: bold; color: #555; background: #f2f2f2; }
  .toolbar { padding: 6px; }
  .toolbar button { font-size: 13px; padding: 6px 14px; cursor: pointer; }
  @media print { .noprint { display: none !important; } }
`

// 印刷後、#content を計測して1ページ（印刷可能領域）に収まるよう縮小する。
const FIT_SCRIPT = `
  (function () {
    function fit() {
      var c = document.getElementById('content')
      if (!c) return
      c.style.transform = 'none'
      var h = c.scrollHeight
      var w = c.scrollWidth
      var scale = Math.min(${PAGE_HEIGHT} / h, ${CONTENT_WIDTH} / w, 1)
      c.style.transform = 'scale(' + scale + ')'
    }
    window.addEventListener('load', function () { fit(); window.focus(); window.print(); })
  })()
`

// シフト帳票を別ウィンドウに描画し、window.print() で印刷する
export const openShiftPrintWindow = (props: PrintViewProps): boolean => {
  const body = renderToStaticMarkup(<PrintView {...props} />)
  const html = `<!doctype html>
<html lang="ja">
<head>
<meta charset="utf-8">
<title>生産シフト帳票 ${props.week.label}</title>
<style>${PRINT_STYLES}</style>
</head>
<body>
  <div class="toolbar noprint"><button onclick="window.print()">印刷</button></div>
  <div id="page"><div id="content">${body}</div></div>
  <script>${FIT_SCRIPT}</script>
</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) return false
  win.document.open()
  win.document.write(html)
  win.document.close()
  return true
}
